/**
 * The chrome, in 繁體中文.
 *
 * `Record<Keys, string>` is the completeness gate: **a missing key fails `tsc`**, so no label can
 * ship in only one language, and it costs one type annotation.
 *
 * What is NOT here is the server's prose. Its sentences arrive in one language and the console
 * doesn't translate them (SPEC_5 §4.5).
 */

import type { Keys } from "./en";

export const zhHant: Record<Keys, string> = {
  // ── the shell ──
  "app.name": "便當AI",
  "app.skip": "跳至今日",
  "lang.en": "EN",
  "lang.zh": "繁",

  // ── sign in ──
  "a1.title": "便當AI",
  "a1.lede": "登入只回答一個問題：你是我們的人嗎？",
  "a1.verifying": "確認中…",
  "a1.email": "電子郵件",
  "a1.password": "密碼",
  "a1.submit": "登入",
  "a1.unanswered": "伺服器沒有回應，沒有完成登入；請再試一次。",
  "a1.idle": "尚未登入。",

  // ── the header ──
  "hdr.competition": "競賽",
  "hdr.stream.connecting": "連線中",
  "hdr.stream.live": "即時",
  "hdr.stream.reconnecting": "重新連線中",
  "hdr.stream.unreachable": "伺服器無法連線",
  "hdr.signOut": "登出",
  "banner.workerDead": "worker 已停止",

  // ── today ──
  "today.title": "今日",
  "today.cutoff": "請在此前送出",
  "today.left": "剩 {span}",
  "today.passed": "已過",
  "today.download": "下載 D-Plan",
  "today.uploading": "上傳中…",
  "today.rerun": "重新執行",
  "today.why": "為什麼要重新執行？（會連同你的名字記錄）",
  "up.act": "上傳",
  "up.button": "上傳 DataPack",
  "up.uploaded": "上傳於",
  "up.by": "由",

  // ── market data (the evidence pack) ─────────────────────────────────────────
  "pack.title": "市場資料",
  "pack.priceNote": "價格註記",

  // ── plan problems ──────────────────────────────────────────────────────────
  "checks.title": "計畫問題",
  "checks.fail": "失敗",
  "checks.warn": "警示",

  // ── the run ──
  "run.title": "AI 做了什麼",
  "run.none": "還沒有執行。",
  "d2.running": "進行中",
  "d3.reply": "回覆",
  "d3.refused": "被拒絕",
  "d3.error": "錯誤",
  "d3.rested": "已休息",
  "d3.analystWorking": "分析師工作中",
  "d3.expected": "這段停頓是正常的：分析師正在沙盒與網路上作業。",
  "d3.elapsed": "已過",
  "log.snapshot": "閱讀開場簡報",
  "log.thinking": "思考",
  "log.recall": "回想筆記",
  "log.remember": "記下筆記",
  "log.note_to_next_run": "留言給下一次執行",
  "log.web_search": "搜尋網路",
  "log.view": "開啟檔案",
  "log.create": "建立檔案",
  "log.edit": "編輯檔案",
  "log.run_command": "執行指令",
  "log.ask_analyst": "詢問分析師",
  "log.read_datapacks": "讀取過往資料包",
  "log.read_dplans": "讀取過往 D-Plan",
  "log.use_skill": "開啟技能",
  "log.submit_plan": "送出計畫",

  // ── our portfolio ──────────────────────────────────────────────────────────
  "pf.title": "我們的投資組合",
  "pf.none": "還沒有已結算的資料包。",
  "pf.nav": "淨值",
  "pf.sinceStart": "自起始 {pct}",
  "pf.cash": "現金",
  "pf.holdings": "持股檔數",
  "pf.warnings": "警告",
  "pf.yesterday": "前一交易日委託：",
  "pf.excluded": "被排除：{codes}",
  "pf.holdingsTable": "持股",
  "pf.code": "代號",
  "pf.name": "名稱",
  "pf.weight": "權重",
  "pf.outside": "不在 150 檔內",
  "pf.overCap": "超過 {cap} 上限",
  "pf.overBuffer": "超過我們的 {cap}",
  "pf.dayOver": "已超過上限 {n} 天",
  "pf.daysOver": "已超過上限 {n} 天",

  // ── skills ──
  "skills.title": "技能",
  "skills.none": "還沒有技能。團隊會在 git 的 backend/skills/ 底下新增。",
  "skills.used": "今日已使用",
  "skills.unused": "未使用",
  "skills.invalid": "無效",

  // ── shared ──
  "ui.loading": "讀取中…",
  "ui.retry": "重試",
  "ui.yourClock": "你的時鐘",
  "ui.language": "語言",
  "ui.na": "—",
  "info.what": "{term} 是什麼？",
  "info.datapack": "主辦單位提供的檔案，內含我們的帳戶、持股與前一日價格。每天晚上約 19:00 後從玉山平台下載（第一天的可能當天早上才出現），並上傳到這裡。AI 會在隔天早上 05:30 開始執行，若較晚上傳則立即開始。",
  "info.dplan": "AI 為今天寫好的交易計畫。下載後上傳到玉山平台，並在 08:40 前按下送出繳交。",
  "info.cutoff": "平台在 {deadline} 截止；我們在 {cutoff} 就停止，留時間處理錯誤。",
  "info.pack": "我們自己抓取的價格、法人買賣與新聞，用來協助 AI 判斷。只有在某個來源失敗時才會顯示。",
  "info.checks": "針對計畫的規則檢查。失敗會擋下計畫；警示可以通過，但值得一讀。",
  "info.run": "AI 今天早上做的每一個步驟。點一下步驟可看細節。",
  "info.skills": "團隊寫好、AI 可以打開的操作手冊，例如我們的多因子策略。",
  "info.portfolio": "主辦單位在最新 DataPack 中回報的我們的帳戶。",
  "info.nav": "基金總價值。起始為 NT$10 億。",
  "info.cash": "未投入股票的比例。規則 0–25%；我們維持 2–20%。",
  "info.holdings": "持有的股票檔數。規則 20–30 檔。",
  "info.warnings": "主辦單位發出的違規警告。累積 3 次即取消資格。",
};
